import { useEffect, useRef } from 'react';

import {
  buildActiveContact,
  buildAgentSnapshot,
  buildHistoryEntry,
  buildQueueSummaries,
  extractQueueName,
  flattenAttributes,
} from './connect-mappers';
import { PendingDisposition } from './ConnectContext';
import {
  ActiveContact,
  AgentSnapshot,
  CallHistoryEntry,
  ConnectInitState,
  ContactAttributes,
  QueueSummary,
  RecordingState,
} from '../../../@types/connect.type';
import { CCP_URL, CONNECT_REGION } from '../../../configs/connect';
import { appendHistory } from '../../../utils/connect-storage';

// Owns the CCP iframe lifecycle: initCCP + agent/contact subscriptions on
// mount, terminate + state reset on unmount. Extracted from ConnectProvider
// to keep the provider file focused on context wiring (and within the
// 250-line file limit). Also handles the duplicate-tab placeholder that
// shows when this tab loses leadership.

interface UseConnectCCPDeps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  agentRef: React.MutableRefObject<connect.Agent | null>;
  contactRef: React.MutableRefObject<connect.Contact | null>;
  hasPermission: boolean;
  userEmail: string;
  isTabLeader: boolean;
  setInitState: React.Dispatch<React.SetStateAction<ConnectInitState>>;
  setAgent: React.Dispatch<React.SetStateAction<AgentSnapshot | null>>;
  setContact: React.Dispatch<React.SetStateAction<ActiveContact | null>>;
  setAttributes: React.Dispatch<React.SetStateAction<ContactAttributes>>;
  setRecordingState: React.Dispatch<React.SetStateAction<RecordingState>>;
  setQueues: React.Dispatch<React.SetStateAction<QueueSummary[]>>;
  setHistory: React.Dispatch<React.SetStateAction<CallHistoryEntry[]>>;
  setPendingDisposition: React.Dispatch<React.SetStateAction<PendingDisposition | null>>;
}

export const useConnectCCP = (deps: UseConnectCCPDeps): void => {
  const {
    containerRef,
    agentRef,
    contactRef,
    hasPermission,
    userEmail,
    isTabLeader,
    setInitState,
    setAgent,
    setContact,
    setAttributes,
    setRecordingState,
    setQueues,
    setHistory,
    setPendingDisposition,
  } = deps;
  const initialisedRef = useRef(false);

  // Reflect duplicate-tab state so consumers can render a placeholder.
  useEffect(() => {
    if (!hasPermission || !userEmail) return;
    if (!isTabLeader && !initialisedRef.current) {
      setInitState({ status: 'duplicateTab', errorMessage: null });
    }
  }, [hasPermission, userEmail, isTabLeader, setInitState]);

  // Boot the CCP iframe once permission + container + user + leadership are
  // all in place. Followers stay parked on the duplicate-tab notice above.
  useEffect(() => {
    if (!hasPermission || !userEmail || !isTabLeader) return;
    if (initialisedRef.current) return;
    const container = containerRef.current;
    if (!container) return;

    initialisedRef.current = true;
    setInitState({ status: 'initialising', errorMessage: null });

    try {
      connect.core.initCCP(container, {
        ccpUrl: CCP_URL,
        loginPopup: true,
        loginPopupAutoClose: true,
        loginOptions: { autoClose: true, height: 600, width: 400 },
        region: CONNECT_REGION,
        softphone: { allowFramedSoftphone: true },
        // Modern browsers block third-party storage in iframes by default.
        // Without an explicit Storage Access API request, the CCP iframe
        // can't persist auth cookies, so the agent's session dies silently
        // and dialing / status changes stop working.
        storageAccess: { canRequest: true, mode: 'default' },
      });

      // Surface init / auth failures as a real error rather than letting
      // the panel sit in 'initialising' forever. Each handler maps to a
      // distinct failure mode an admin can act on (re-login, fix approved
      // origins list, etc.).
      connect.core.onAuthFail(() => {
        setInitState({
          status: 'error',
          errorMessage: 'Amazon Connect authentication failed — please log in again.',
        });
      });
      connect.core.onAccessDenied(() => {
        setInitState({
          status: 'error',
          errorMessage: 'Access denied by Amazon Connect — check your Connect agent permissions.',
        });
      });
      connect.core.onIframeRetriesExhausted(() => {
        setInitState({
          status: 'error',
          errorMessage:
            'Amazon Connect iframe failed to load — verify this origin is on the instance Approved Origins list.',
        });
      });

      connect.agent((agentApi) => {
        agentRef.current = agentApi;
        setAgent(buildAgentSnapshot(agentApi));
        setQueues(buildQueueSummaries(agentApi));
        agentApi.onStateChange(() => setAgent(buildAgentSnapshot(agentApi)));
        agentApi.onRoutable(() => setAgent(buildAgentSnapshot(agentApi)));
        agentApi.onOffline(() => setAgent(buildAgentSnapshot(agentApi)));
        setInitState({ status: 'ready', errorMessage: null });
      });

      connect.contact((contactApi) => {
        contactRef.current = contactApi;
        setContact(buildActiveContact(contactApi, 'connecting'));
        setAttributes(flattenAttributes(contactApi.getAttributes()));
        // Most calls record by default; we don't actually know the state
        // without a server check, but resetting to `recording` matches
        // typical agent expectation.
        setRecordingState('recording');
        contactApi.onConnected((c) => {
          setContact(buildActiveContact(c, 'connected'));
          setAttributes(flattenAttributes(c.getAttributes()));
        });
        const finishContact = (c: connect.Contact, outcome: 'completed' | 'missed') => {
          const active = buildActiveContact(c, 'ended');
          const endedAt = new Date().toISOString();
          const entry = buildHistoryEntry(active, outcome, endedAt);
          if (userEmail) setHistory(appendHistory(userEmail, entry));
          // Only prompt for disposition on calls that actually connected —
          // missed calls have nothing for the agent to wrap up.
          if (outcome === 'completed') {
            setPendingDisposition({
              contactId: active.contactId,
              initialContactId: c.getOriginalContactId() ?? active.contactId,
              channel: 'voice',
              queueName: extractQueueName(c),
              endedAt,
            });
          }
          setContact(null);
          setAttributes({});
          setRecordingState('unknown');
          contactRef.current = null;
        };
        contactApi.onEnded((c) => finishContact(c, 'completed'));
        contactApi.onMissed((c) => finishContact(c, 'missed'));
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Connect init failed';
      setInitState({ status: 'error', errorMessage: message });
    }

    return () => {
      try {
        connect.core.terminate();
      } catch {
        // terminate() throws if init never completed — safe to ignore.
      }
      initialisedRef.current = false;
      agentRef.current = null;
      contactRef.current = null;
      // Clear consumer-visible snapshots so a permission revoke or tab-
      // leadership flip doesn't leave stale agent/contact state in context.
      setInitState({ status: 'uninitialised', errorMessage: null });
      setAgent(null);
      setContact(null);
      setAttributes({});
      setRecordingState('unknown');
      setQueues([]);
    };
  }, [hasPermission, userEmail, isTabLeader]);
};
